import React, { forwardRef, useImperativeHandle, useMemo } from "react";
import { Typography, Table, Col } from "antd";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import Comment from "components/Comments";
import CiResultDisplay from "./CiResultDisplay";
import { renderDpatEvidenceLink } from "utils/renderDpatEvidenceLink";

const { Title } = Typography;

const MEETING_LINK_KEYS = ["docs", "invitation", "recommendation"];

const LINK_LABELS = {
  docs: "View Minutes",
  invitation: "View Attendance",
  recommendation: "View Document",
};

const TownHollMeeting = forwardRef(({
  meetings,
  year,
  columns,
  districtId,
  hideComment,
  savedAssessmentData,
  isAssessmentStarted,
}, ref) => {
  const displayMeetings = useMemo(() => {
    if (!isAssessmentStarted || !savedAssessmentData) return meetings;
    return savedAssessmentData.meetings ?? savedAssessmentData;
  }, [isAssessmentStarted, savedAssessmentData, meetings]);

  const meetingRows = displayMeetings?.data ?? [];

  const meetingTableColumns = useMemo(
    () => (columns ?? []).map((col) =>
      MEETING_LINK_KEYS.includes(col.dataIndex)
        ? {
            ...col,
            render: (value) => renderDpatEvidenceLink(
              value,
              LINK_LABELS[col.dataIndex] ?? "View Document",
            ),
          }
        : col
    ),
    [columns],
  );

  const DPAT_SLUG = "c4.0-4.4";

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "CI4",
      area: "Transparency, Accountability & Participation",
      meetings: displayMeetings,
    }),
  }));

  return (
    <Comment
      data={displayMeetings?.data}
      year={year}
      districtId={districtId}
      tableCommentedId={`c4.0-4.4-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
        <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
          />

          <Col align="start">
            <CiResultDisplay slug={DPAT_SLUG} fulfillment={displayMeetings?.fulfillment} />
          </Col>

          <Title level={4} style={{ marginTop: "20px" }}>
            Town Hall Meeting Evidence with Reports
          </Title>

          {meetingRows.length > 0 && (
            <Table
              columns={meetingTableColumns}
              dataSource={meetingRows}
              pagination={false}
              bordered
              rowKey={(record, index) => record.key || index}
            />
          )}
          {!hideComment && renderCommentFeedbackFooter()}
        </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default TownHollMeeting;
